// Phase 7 sub-bucket 17 — Saved Views catalog hoist.
//
// Pure data + UI helpers shared by the worker (apps/api) and the SPA
// (apps/web). Lives in @colab/shared so the SPA's FilterTreeBuilder /
// ConfigureColumns primitives can read the field catalog directly without
// reaching into worker-only code.
//
// Sourced verbatim from:
//   - apps/api/src/services/segments.ts (SEGMENT_FIELDS, OPERATORS_BY_TYPE,
//     OPERATOR_LABEL, VALUELESS_OPERATORS, makeCondition, makeGroup,
//     defaultFilter, countConditions, findField, isFilterGroup,
//     FILTER_SCHEMA_VERSION)
//   - apps/api/src/services/views.ts (VIEW_COLUMN_CATALOG, VIEW_COLUMN_LABEL,
//     reconcileColumns, visibleColumnKeys, isValidSort,
//     parseFilterFromSearchParam)
//
// `validateFilterTree` deliberately stays in apps/api/src/services/segments.ts —
// it's only called by worker write paths (createSegment, createView,
// updateView). Shipping it into the SPA bundle would add ~150 LOC of
// validator code for no consumer; the worker re-validates every write.

import {
  ASSET_TYPES,
  ASSET_TYPE_LABEL,
  ASSET_VISIBILITIES,
  ASSET_VISIBILITY_LABEL,
  DEAL_STATUSES,
  DEAL_STATUS_LABEL,
  DEAL_TYPES,
  DEAL_TYPE_LABEL,
  EMBED_PROVIDERS,
  EMBED_PROVIDER_LABEL,
  JOURNEY_EXECUTION_ORDERS,
  JOURNEY_EXECUTION_ORDER_LABEL,
  MDF_REQUEST_STATUSES,
  MDF_REQUEST_STATUS_LABEL,
  PARTNER_STATUSES,
  PARTNER_STATUS_LABEL,
  PARTNER_TIERS,
  PARTNER_TIER_LABEL,
  PARTNER_TYPES,
  PARTNER_TYPE_LABEL,
  SUBMISSION_STATUSES,
  SUBMISSION_STATUS_LABEL,
  SUBMISSION_DOWNSTREAM_KINDS,
  type FilterCondition,
  type FilterGroup,
  type FilterNode,
  type FilterOperator,
  type FilterTree,
  type ViewColumnSpec,
  type ViewEntityType,
  type ViewSort,
} from "./domain";

export const FILTER_SCHEMA_VERSION = 1 as const;

// ---------------------------------------------------------------------------
// Field metadata (read by FilterTreeBuilder).
// ---------------------------------------------------------------------------

export type FieldType = "text" | "number" | "date" | "enum" | "boolean";

export type FieldDef = {
  key: string;
  label: string;
  type: FieldType;
  enumValues?: readonly string[];
  enumLabels?: Readonly<Record<string, string>>;
};

// Note: index signature is `ViewEntityType` (superset of `SegmentEntityType`)
// so admin-only entities like `admin_deal` can carry filter fields even
// though segments aren't supported for them.
export const SEGMENT_FIELDS: Record<ViewEntityType, readonly FieldDef[]> = {
  partner: [
    {
      key: "tier",
      label: "Tier",
      type: "enum",
      enumValues: PARTNER_TIERS,
      enumLabels: PARTNER_TIER_LABEL,
    },
    {
      key: "status",
      label: "Status",
      type: "enum",
      enumValues: PARTNER_STATUSES,
      enumLabels: PARTNER_STATUS_LABEL,
    },
    {
      key: "partner_type",
      label: "Type",
      type: "enum",
      enumValues: PARTNER_TYPES,
      enumLabels: PARTNER_TYPE_LABEL,
    },
    { key: "company_name", label: "Company name", type: "text" },
    { key: "region", label: "Region", type: "text" },
    { key: "created_at", label: "Created at", type: "date" },
  ],
  deal: [
    {
      key: "status",
      label: "Status",
      type: "enum",
      enumValues: DEAL_STATUSES,
      enumLabels: DEAL_STATUS_LABEL,
    },
    {
      key: "deal_type",
      label: "Type",
      type: "enum",
      enumValues: DEAL_TYPES,
      enumLabels: DEAL_TYPE_LABEL,
    },
    { key: "estimated_value_eur", label: "Estimated value (EUR)", type: "number" },
    { key: "prospect_company", label: "Prospect company", type: "text" },
    { key: "created_at", label: "Created at", type: "date" },
  ],
  // Phase 7 sub-bucket 19 — `form_id` / `partner_id` were UUID-only text
  // fields that admins couldn't realistically search against. The catalog now
  // exposes `form_name` / `partner_name` (denormalised onto every row by
  // `listAdminSubmissions`); the worker post-filters those two columns
  // in-memory via `matchAdminSubmission` (the sub-bucket 18 `matchAdminDeal`
  // pattern), while status / dates / downstream stay server-side via
  // `applyFilterToQuery`. Volume parity with admin_deal (~hundreds) makes
  // in-memory post-filter the cheap, correct choice.
  submission: [
    {
      key: "status",
      label: "Status",
      type: "enum",
      enumValues: SUBMISSION_STATUSES,
      enumLabels: SUBMISSION_STATUS_LABEL,
    },
    { key: "form_name", label: "Form", type: "text" },
    { key: "partner_name", label: "Partner", type: "text" },
    { key: "created_at", label: "Submitted at", type: "date" },
    { key: "decided_at", label: "Decided at", type: "date" },
    {
      key: "downstream_object_kind",
      label: "Downstream",
      type: "enum",
      enumValues: SUBMISSION_DOWNSTREAM_KINDS,
      enumLabels: {
        deal: "Deal",
        lead: "Lead",
        partner_application: "Partner application",
        contact: "Contact",
        company: "Company",
      },
    },
  ],
  journey: [
    { key: "name", label: "Name", type: "text" },
    {
      key: "execution_order",
      label: "Execution order",
      type: "enum",
      enumValues: JOURNEY_EXECUTION_ORDERS,
      enumLabels: JOURNEY_EXECUTION_ORDER_LABEL,
    },
    { key: "is_archived", label: "Archived", type: "boolean" },
    { key: "created_at", label: "Created at", type: "date" },
    { key: "updated_at", label: "Last updated", type: "date" },
  ],
  content_asset: [
    { key: "title", label: "Title", type: "text" },
    {
      key: "asset_type",
      label: "Type",
      type: "enum",
      enumValues: ASSET_TYPES,
      enumLabels: ASSET_TYPE_LABEL,
    },
    {
      key: "embed_provider",
      label: "Provider",
      type: "enum",
      enumValues: EMBED_PROVIDERS,
      enumLabels: EMBED_PROVIDER_LABEL,
    },
    {
      key: "visibility",
      label: "Visibility",
      type: "enum",
      enumValues: ASSET_VISIBILITIES,
      enumLabels: ASSET_VISIBILITY_LABEL,
    },
    { key: "archived_at", label: "Archived at", type: "date" },
    { key: "created_at", label: "Created at", type: "date" },
    { key: "updated_at", label: "Last updated", type: "date" },
  ],
  mdf_request: [
    {
      key: "status",
      label: "Status",
      type: "enum",
      enumValues: MDF_REQUEST_STATUSES,
      enumLabels: MDF_REQUEST_STATUS_LABEL,
    },
    { key: "partner_id", label: "Partner", type: "text" },
    { key: "fund_id", label: "Fund", type: "text" },
    { key: "campaign_name", label: "Campaign name", type: "text" },
    { key: "campaign_type", label: "Campaign type", type: "text" },
    { key: "amount_eur", label: "Amount (EUR)", type: "number" },
    { key: "created_at", label: "Submitted at", type: "date" },
    { key: "approved_at", label: "Approved at", type: "date" },
  ],
  // Phase 7 sub-bucket 18 — admin_deal Saved Views catalog. Targets the
  // HubSpot Sales-pipeline kanban (`hubspot_deals` + `hubspot_deal_attributions`
  // + `partners` join), not the partner-submitted `deals` table. Fields are
  // mostly text/number/date because HubSpot stage IDs are workspace-specific
  // and can't be hardcoded as an enum. `direction` is the synthetic
  // inbound/outbound projection of the attribution relationship.
  admin_deal: [
    { key: "deal_stage_id", label: "Stage", type: "text" },
    {
      key: "direction",
      label: "Direction",
      type: "enum",
      enumValues: ["inbound", "outbound"] as const,
      enumLabels: { inbound: "Inbound", outbound: "Outbound" },
    },
    { key: "partner_id", label: "Partner", type: "text" },
    { key: "owner_id", label: "Owner", type: "text" },
    { key: "deal_amount", label: "Amount (EUR)", type: "number" },
    { key: "deal_close_date", label: "Close date", type: "date" },
  ],
};

export const OPERATORS_BY_TYPE: Record<FieldType, readonly FilterOperator[]> = {
  text: ["equals", "not_equals", "contains", "not_contains", "starts_with", "is_null", "is_not_null"],
  number: ["equals", "not_equals", "gt", "gte", "lt", "lte", "between", "is_null", "is_not_null"],
  date: [
    "equals",
    "not_equals",
    "gt",
    "gte",
    "lt",
    "lte",
    "between",
    "in_last_days",
    "in_next_days",
    "is_null",
    "is_not_null",
  ],
  enum: ["equals", "not_equals", "in", "not_in", "is_null", "is_not_null"],
  boolean: ["equals", "not_equals"],
};

export const OPERATOR_LABEL: Record<FilterOperator, string> = {
  equals: "is",
  not_equals: "is not",
  in: "is any of",
  not_in: "is none of",
  contains: "contains",
  not_contains: "does not contain",
  starts_with: "starts with",
  gt: "greater than",
  gte: "greater than or equal",
  lt: "less than",
  lte: "less than or equal",
  between: "between",
  is_null: "is empty",
  is_not_null: "is not empty",
  in_last_days: "in the last N days",
  in_next_days: "in the next N days",
};

export const VALUELESS_OPERATORS: ReadonlySet<FilterOperator> = new Set([
  "is_null",
  "is_not_null",
]);

// ---------------------------------------------------------------------------
// Factories + small utilities.
// ---------------------------------------------------------------------------

export function defaultFilter(_entityType: ViewEntityType): FilterTree {
  void _entityType;
  return {
    kind: "group",
    version: FILTER_SCHEMA_VERSION,
    combinator: "AND",
    children: [],
  };
}

export function makeCondition(
  field: FieldDef,
  operator?: FilterOperator,
): FilterCondition {
  const op = operator ?? OPERATORS_BY_TYPE[field.type][0] ?? "equals";
  return {
    kind: "condition",
    version: FILTER_SCHEMA_VERSION,
    field: field.key,
    operator: op,
    value: field.type === "boolean" ? true : undefined,
  };
}

export function makeGroup(combinator: "AND" | "OR" = "AND"): FilterGroup {
  return {
    kind: "group",
    version: FILTER_SCHEMA_VERSION,
    combinator,
    children: [],
  };
}

export function countConditions(node: FilterNode | null | undefined): number {
  if (!node) return 0;
  if (node.kind === "condition") return 1;
  return node.children.reduce((sum, c) => sum + countConditions(c), 0);
}

export function findField(
  entityType: ViewEntityType,
  fieldKey: string,
): FieldDef | undefined {
  return SEGMENT_FIELDS[entityType]?.find((f) => f.key === fieldKey);
}

export function isFilterGroup(value: unknown): value is FilterGroup {
  return (
    typeof value === "object" &&
    value !== null &&
    (value as { kind?: unknown }).kind === "group"
  );
}

// ---------------------------------------------------------------------------
// Column-spec catalog + helpers (read by ConfigureColumns + list pages).
// ---------------------------------------------------------------------------

export const VIEW_COLUMN_CATALOG: Record<ViewEntityType, ViewColumnSpec[]> = {
  partner: [
    { key: "company_name", visible: true },
    { key: "partner_type", visible: true },
    { key: "tier", visible: true },
    { key: "status", visible: true },
    { key: "region", visible: true },
    { key: "deal_count", visible: false },
    { key: "intro_count", visible: false },
    { key: "primary_contact_email", visible: false },
    { key: "created_at", visible: false },
    { key: "updated_at", visible: true },
  ],
  deal: [
    { key: "title", visible: true },
    { key: "status", visible: true },
    { key: "deal_type", visible: true },
    { key: "estimated_value_eur", visible: true },
    { key: "prospect_company", visible: true },
    { key: "created_at", visible: false },
    { key: "updated_at", visible: true },
  ],
  submission: [
    { key: "form_name", visible: true },
    { key: "partner_name", visible: true },
    { key: "status", visible: true },
    { key: "submitted_at", visible: true },
    { key: "decided_at", visible: true },
    { key: "decided_by", visible: false },
    { key: "downstream_kind", visible: false },
  ],
  journey: [
    { key: "name", visible: true },
    { key: "task_count", visible: true },
    { key: "assignment_count", visible: true },
    { key: "execution_order", visible: true },
    { key: "updated_at", visible: true },
    { key: "created_at", visible: false },
  ],
  content_asset: [
    { key: "title", visible: true },
    { key: "asset_type", visible: true },
    { key: "embed_provider", visible: true },
    { key: "visibility", visible: true },
    { key: "categories", visible: true },
    { key: "views_count", visible: true },
    { key: "updated_at", visible: true },
    { key: "created_by", visible: true },
    { key: "archived_at", visible: false },
  ],
  mdf_request: [
    { key: "campaign_name", visible: true },
    { key: "partner", visible: true },
    { key: "fund", visible: true },
    { key: "status", visible: true },
    { key: "amount_eur", visible: true },
    { key: "campaign_type", visible: false },
    { key: "created_at", visible: true },
    { key: "approved_at", visible: false },
  ],
  admin_deal: [
    { key: "prospect_company", visible: true },
    { key: "deal_stage_label", visible: true },
    { key: "partner_name", visible: true },
    { key: "direction", visible: true },
    { key: "deal_amount", visible: true },
    { key: "deal_close_date", visible: true },
    { key: "owner_name", visible: false },
  ],
};

export const VIEW_COLUMN_LABEL: Record<string, string> = {
  company_name: "Company",
  partner_type: "Type",
  tier: "Tier",
  status: "Status",
  region: "Region",
  deal_count: "Deals",
  intro_count: "Intros",
  primary_contact_email: "Primary contact",
  created_at: "Created",
  updated_at: "Last activity",
  title: "Title",
  deal_type: "Deal type",
  estimated_value_eur: "Estimated value (EUR)",
  prospect_company: "Prospect company",
  form_name: "Form",
  partner_name: "Partner",
  submitted_at: "Submitted",
  decided_at: "Decided",
  decided_by: "Decided by",
  downstream_kind: "Downstream",
  name: "Name",
  task_count: "Tasks",
  assignment_count: "Assignments",
  execution_order: "Execution order",
  asset_type: "Type",
  embed_provider: "Provider",
  visibility: "Visibility",
  categories: "Categories",
  views_count: "Views",
  created_by: "Created by",
  archived_at: "Archived",
  campaign_name: "Campaign",
  partner: "Partner",
  fund: "Fund",
  amount_eur: "Amount (EUR)",
  campaign_type: "Campaign type",
  approved_at: "Approved",
  // Phase 7 sub-bucket 18 — admin_deal columns + filter fields. `partner_id`
  // and `prospect_company` already live above (mdf_request + deal); the
  // admin_deal catalog reuses those labels rather than re-declaring.
  deal_stage_id: "Stage",
  deal_stage_label: "Stage",
  direction: "Direction",
  owner_id: "Owner",
  owner_name: "Owner",
  deal_amount: "Amount (EUR)",
  deal_close_date: "Close date",
};

export function reconcileColumns(
  stored: ViewColumnSpec[] | null | undefined,
  entityType: ViewEntityType,
): ViewColumnSpec[] {
  const catalog = VIEW_COLUMN_CATALOG[entityType] ?? [];
  const storedList = Array.isArray(stored) ? stored : [];
  const catalogKeys = new Set(catalog.map((c) => c.key));
  const storedKeys = new Set<string>();

  const merged: ViewColumnSpec[] = [];
  for (const col of storedList) {
    if (!catalogKeys.has(col.key)) continue;
    storedKeys.add(col.key);
    merged.push({
      key: col.key,
      visible: !!col.visible,
      ...(typeof col.width === "number" ? { width: col.width } : {}),
    });
  }
  for (const cat of catalog) {
    if (storedKeys.has(cat.key)) continue;
    merged.push({ ...cat });
  }
  return merged;
}

export function visibleColumnKeys(
  stored: ViewColumnSpec[] | null | undefined,
  entityType: ViewEntityType,
): string[] {
  return reconcileColumns(stored, entityType)
    .filter((c) => c.visible)
    .map((c) => c.key);
}

// ---------------------------------------------------------------------------
// Sort helpers.
// ---------------------------------------------------------------------------

export function isValidSort(
  sort: unknown,
  entityType: ViewEntityType,
): sort is ViewSort {
  if (!sort || typeof sort !== "object") return false;
  const candidate = sort as { column?: unknown; direction?: unknown };
  if (typeof candidate.column !== "string") return false;
  if (candidate.direction !== "asc" && candidate.direction !== "desc")
    return false;
  const catalog = VIEW_COLUMN_CATALOG[entityType] ?? [];
  const allowed = new Set<string>([
    ...catalog.map((c) => c.key),
    "created_at",
    "updated_at",
    "name",
  ]);
  return allowed.has(candidate.column);
}

// ---------------------------------------------------------------------------
// URL filter param parser.
// ---------------------------------------------------------------------------

export function parseFilterFromSearchParam(
  raw: string | undefined | null,
): unknown {
  if (!raw) return null;
  try {
    return JSON.parse(decodeURIComponent(raw));
  } catch {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }
}
